import React from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon } from "lucide-react";
import { Button } from "../../ui/button";
import { cn } from "../../../lib/utils";
import PaginatedTable, { PaginatedTableColumn } from "../../ui/paginated-table";

interface CompositionMember {
  dataset_id: number;
  dataset_name: string;
  dataset_description: string;
  sampling_type: string;
  sampling_weight: number;
  origin_dataset_id: number;
  origin_dataset_name: string;
  origin_dataset_description: string;
  request_count: number;
  major_version?: number;
  minor_version?: number;
}

interface CompositionMembersTableProps {
  members: CompositionMember[];
  loading: boolean;
  totalRequestCount: number;
  emptyTitle?: string;
  emptyDescription?: string;
}

const CompositionMembersTable: React.FC<CompositionMembersTableProps> = ({
  members,
  loading,
  totalRequestCount,
  emptyTitle = "No member datasets found",
  emptyDescription = "This composition has no member datasets."
}) => {
  const navigate = useNavigate();

  const getSamplingTypeColor = (type: string) => {
    switch (type.toUpperCase()) {
      case "REQUEST":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "SESSION":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const columns: PaginatedTableColumn<CompositionMember>[] = [
    {
      key: "origin_dataset_name",
      header: "Dataset",
      width: "w-[35%]",
      render: (member) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
            {member.origin_dataset_name}
          </div>
          <div className="text-xs text-muted-foreground">
            {member.origin_dataset_description || "No description"}
          </div>
        </div>
      )
    },
    {
      key: "major_version",
      header: "Version",
      width: "w-[15%]",
      render: (member) => (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          v{member.major_version || 1}.{member.minor_version || 0}
        </span>
      )
    },
    {
      key: "sampling_type",
      header: "Sampling",
      width: "w-[20%]",
      render: (member) => (
        <span className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getSamplingTypeColor(member.sampling_type)
        )}>
          {member.sampling_weight}% by {member.sampling_type.toLowerCase()}
        </span>
      )
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[15%]",
      render: (member) => (
        <span className="text-sm font-gc-med text-foreground">
          {member.request_count.toLocaleString()}
        </span>
      )
    },
    {
      key: "dataset_id",
      header: "Percentage",
      width: "w-[10%]",
      render: (member) => {
        const percentage = totalRequestCount > 0 
          ? Math.round((member.request_count / totalRequestCount) * 100) 
          : 0;
        return (
          <span className="text-sm font-gc-reg text-foreground">
            {percentage}%
          </span>
        );
      }
    },
    {
      key: "origin_dataset_id",
      header: "Actions",
      width: "w-[5%]",
      render: (member) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/forge/datasets/${member.origin_dataset_id}`);
          }}
        >
          <EyeIcon className="w-3 h-3" />
          View
        </Button>
      )
    }
  ];

  return (
    <PaginatedTable
      data={members}
      columns={columns}
      loading={loading}
      currentPage={1}
      onPageChange={() => {}} // No pagination needed for this table
      pageSize={members.length}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={(member) => navigate(`/forge/datasets/${member.origin_dataset_id}`)}
      rowClassName="cursor-pointer hover:bg-accent/20 transition-colors"
      showPagination={false}
      loadingRows={5}
    />
  );
};

export default CompositionMembersTable; 